import { useEffect, useState, type FormEvent } from 'react';
import { errorText } from '../api';
import { formatDateTimeShort } from '../format';
import { adminApi, type ApiKeyRow, type AuditEntry, type ConnectionRow, type ScopeInfo } from './api';

/**
 * „KI verbinden“ (Konzept 9.2): Adresse des Anschlusses in Claude eintragen, auf der Erlauben-Seite anmelden –
 * fertig. Hier stehen die Anleitung, die bestehenden Verbindungen (mit Trennen), die Schlüssel für den lokalen
 * Weg (Claude Code) und das Protokoll.
 */
export function ConnectPage() {
  const mcpUrl = `${window.location.origin}/mcp`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(mcpUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <>
      <div className="admin-head">
        <div>
          <h1>KI verbinden</h1>
          <p className="muted">Lass Claude deine Termine im Blick behalten: Buchungen ansehen, Tage sperren oder freigeben, Termine absagen. Du entscheidest beim Verbinden, was erlaubt ist, und kannst jede Verbindung hier wieder trennen.</p>
        </div>
      </div>
      <div className="admin-grid">
        <div className="card">
          <h2 className="card__title">Deine Adresse für KI-Assistenten</h2>
          <div className="row">
            <input type="text" readOnly value={mcpUrl} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="button button--secondary button--small" onClick={copy}>
              {copied ? 'Kopiert' : 'Kopieren'}
            </button>
          </div>
          <p className="muted small">Die Adresse ist kein Geheimnis – ohne deinen Login kann niemand etwas damit anfangen.</p>
        </div>
        <div className="card">
          <h2 className="card__title">So verbindest du Claude</h2>
          <ol className="steps-list">
            <li>
              In Claude (claude.ai oder die Claude-App) auf <strong>Einstellungen → Connectors</strong> gehen und <strong>„Eigenen Connector hinzufügen“</strong> wählen.
            </li>
            <li>
              Name: <strong>EverCal</strong>, Adresse: die oben kopierte.
            </li>
            <li>
              Auf <strong>Verbinden</strong> klicken. Es öffnet sich deine Erlauben-Seite: anmelden, Häkchen setzen, <strong>Erlauben</strong>.
            </li>
            <li>
              Zurück in Claude einfach loslegen: <em>„Welche Termine habe ich nächste Woche?“</em> oder <em>„Sperr den 24. bis 28. November, Urlaub.“</em>
            </li>
          </ol>
          <p className="muted small">
            Claude Code im Terminal: <code>claude mcp add --transport http evercal {mcpUrl}</code>
          </p>
        </div>
        <ConnectionsCard />
        <div className="card">
          <h2 className="card__title">Was die KI kann – und was nicht</h2>
          <ul className="plain-list">
            <li>
              <strong>Lesen:</strong> Auftritte, Terminarten, Wochenraster, freie Zeiten, Buchungen und deine Einträge unter „Tage“.
            </li>
            <li>
              <strong>Zeiten:</strong> einzelne Tage oder Zeiträume sperren, Zeiten zusätzlich freigeben, solche Einträge wieder entfernen.
            </li>
            <li>
              <strong>Buchungen:</strong> einen Termin absagen – der Kunde bekommt sofort die Mail mit deiner Nachricht.
            </li>
            <li>
              <strong>Nie:</strong> umbuchen, Wochenraster ändern, Terminarten oder Auftritte anlegen, Passwort oder Schlüssel anfassen. Das richtest du einmal hier ein.
            </li>
          </ul>
          <p className="muted small">Jede Änderung über die KI steht im Protokoll weiter unten.</p>
        </div>
        <ApiKeysCard />
        <AuditCard />
      </div>
    </>
  );
}

function ConnectionsCard() {
  const [rows, setRows] = useState<ConnectionRow[] | null>(null);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = () =>
    adminApi
      .listConnections()
      .then((r) => {
        setRows(r.connections);
        setScopes(r.scopes);
      })
      .catch((e) => setError(errorText(e)));
  useEffect(() => void load(), []);
  const title = (s: string) => scopes.find((x) => x.scope === s)?.title ?? s;

  async function disconnect(c: ConnectionRow) {
    if (!window.confirm(`Verbindung „${c.clientName ?? 'Unbenannt'}“ trennen? Der Assistent muss sich dann neu verbinden.`)) return;
    try {
      await adminApi.disconnect(c.clientId);
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  }

  return (
    <div className="card">
      <h2 className="card__title">Verbundene Assistenten</h2>
      {rows === null && !error && <p className="muted small">Lade …</p>}
      {rows?.length === 0 && <p className="muted small">Noch keine Verbindung. Sobald ein Assistent verbunden ist, steht er hier.</p>}
      <ul className="plain-list plain-list--rows">
        {rows?.map((c) => (
          <li key={c.clientId}>
            <div>
              <strong>{c.clientName ?? 'Unbenannt'}</strong> {c.active ? <span className="tag tag--ok">verbunden</span> : <span className="tag tag--muted">getrennt</span>}
              <div className="small muted">
                {c.scopes.map(title).join(', ')} · seit {formatDateTimeShort(c.connectedAt)}
                {c.lastUsedAt && <> · zuletzt {formatDateTimeShort(c.lastUsedAt)}</>}
              </div>
            </div>
            {c.active && (
              <button type="button" className="link-button" onClick={() => void disconnect(c)}>
                Trennen
              </button>
            )}
          </li>
        ))}
      </ul>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

/** Schlüssel für den lokalen Weg (Claude Code) oder eigene Programme: einmal angezeigt, jederzeit widerrufbar. */
function ApiKeysCard() {
  const [keys, setKeys] = useState<ApiKeyRow[] | null>(null);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>(['zeiten']);
  const [expires, setExpires] = useState('');
  const [fresh, setFresh] = useState<{ key: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () =>
    adminApi
      .listApiKeys()
      .then((r) => {
        setKeys(r.keys);
        setScopes(r.scopes);
      })
      .catch((e) => setError(errorText(e)));
  useEffect(() => void load(), []);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.createApiKey(name.trim() || 'Schlüssel', chosen, expires ? Number(expires) : null);
      setFresh({ key: r.key, name: r.row.name });
      setCopied(false);
      setName('');
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k: ApiKeyRow) {
    if (!window.confirm(`Schlüssel „${k.name}“ widerrufen? Wer ihn nutzt, verliert sofort den Zugriff.`)) return;
    try {
      await adminApi.revokeApiKey(k.id);
      await load();
    } catch (e) {
      setError(errorText(e));
    }
  }

  const active = keys?.filter((k) => !k.revokedAt) ?? [];
  return (
    <div className="card card--wide">
      <h2 className="card__title">Schlüssel für Claude Code und eigene Programme</h2>
      <p className="muted small">Den Weg oben braucht keinen Schlüssel. Einen Schlüssel brauchst du nur für Claude Code auf deinem Rechner (Anleitung: <code>docs/ki-verbinden.md</code>) oder für andere Programme, etwa das Erfolgsmagneten OS. Er wird genau einmal angezeigt und kann nur, was du ankreuzt.</p>
      {fresh && (
        <div className="notice">
          <strong>Dein neuer Schlüssel „{fresh.name}“ – kopiere ihn jetzt, er wird nicht noch einmal angezeigt:</strong>
          <div className="row" style={{ marginTop: 8 }}>
            <code className="keybox" style={{ flex: 1 }}>
              {fresh.key}
            </code>
            <button type="button" className="button button--small" onClick={() => void navigator.clipboard?.writeText(fresh.key).then(() => setCopied(true))}>
              {copied ? 'Kopiert' : 'Kopieren'}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setFresh(null);
                setCopied(false);
              }}
            >
              Ausblenden
            </button>
          </div>
        </div>
      )}
      {active.length > 0 && (
        <ul className="plain-list plain-list--rows">
          {active.map((k) => (
            <li key={k.id}>
              <div>
                <strong>{k.name}</strong> <code className="small">{k.prefix}…</code>
                <div className="small muted">
                  {k.scopes.map((sc) => scopes.find((x) => x.scope === sc)?.title ?? sc).join(', ')}
                  {' · '}
                  {k.lastUsedAt ? `zuletzt ${formatDateTimeShort(k.lastUsedAt)}` : 'noch nicht benutzt'}
                  {' · '}
                  {k.expiresAt ? `gültig bis ${formatDateTimeShort(k.expiresAt)}` : 'unbegrenzt'}
                </div>
              </div>
              <button type="button" className="link-button" onClick={() => void revoke(k)}>
                Widerrufen
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={create} className="stack" style={{ gap: 12 }}>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="key-name">Name</label>
            <input id="key-name" type="text" placeholder="z. B. Claude Code auf dem Mac" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="key-expires">Gültig für</label>
            <select id="key-expires" value={expires} onChange={(e) => setExpires(e.target.value)}>
              <option value="">Unbegrenzt</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="365">1 Jahr</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label>Was der Schlüssel darf</label>
          <div className="checks">
            {scopes.map((sc) => (
              <label key={sc.scope} className="choice">
                <input type="checkbox" checked={sc.scope === 'read' || chosen.includes(sc.scope)} disabled={sc.scope === 'read'} onChange={(e) => setChosen(e.target.checked ? [...chosen, sc.scope] : chosen.filter((x) => x !== sc.scope))} />
                <span>
                  <strong>{sc.title}</strong> <span className="muted small">– {sc.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        {error && <div className="error">{error}</div>}
        <div>
          <button type="submit" className="button button--small" disabled={busy}>
            Schlüssel anlegen
          </button>
        </div>
      </form>
    </div>
  );
}

function AuditCard() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  useEffect(() => {
    adminApi
      .auditLog(50)
      .then((r) => setEntries(r.entries))
      .catch(() => setEntries([]));
  }, []);
  return (
    <div className="card card--wide">
      <h2 className="card__title">Protokoll</h2>
      <p className="muted small">Die letzten Änderungen – egal ob von dir in der Verwaltung oder von einem Assistenten.</p>
      {entries === null && <p className="muted small">Lade …</p>}
      {entries?.length === 0 && <p className="muted small">Noch nichts passiert.</p>}
      <ul className="plain-list plain-list--rows">
        {entries?.map((e, i) => (
          <li key={i}>
            <div>
              <strong>{e.summary}</strong>
              <div className="small muted">
                {e.actorLabel ?? (e.actorKind === 'login' ? 'Login' : 'Schlüssel')} · {formatDateTimeShort(e.at)}
              </div>
            </div>
            {e.status >= 400 && <span className="tag tag--blocked">abgelehnt</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
